import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import useIsMobile from 'hooks/use-is-mobile';

interface NavbarCollapseContextType {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
}

const NavbarCollapseContext = createContext<NavbarCollapseContextType | null>(null);

const COLLAPSE_STORAGE_KEY = 'navbar-collapsed';

interface NavbarCollapseProviderProps {
  children: ReactNode;
}

export const NavbarCollapseProvider = ({ children }: NavbarCollapseProviderProps) => {
  const { isDesktop } = useIsMobile();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return stored === 'true';
  });

  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, isCollapsed.toString());
  }, [isCollapsed]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const effectiveIsCollapsed = isDesktop ? isCollapsed : false;

  return (
    <NavbarCollapseContext.Provider
      value={{
        isCollapsed: effectiveIsCollapsed,
        toggleCollapsed
      }}>
      {children}
    </NavbarCollapseContext.Provider>
  );
};

export const useNavbarCollapse = (): NavbarCollapseContextType => {
  const context = useContext(NavbarCollapseContext);
  if (!context) {
    throw new Error('useNavbarCollapse must be used within a NavbarCollapseProvider');
  }
  return context;
};
